import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ListTable, { ListTableActions, ListTableColumn } from '../../../shared/listView/ListTable';
import ListTableNameCell from '../../../shared/listView/ListTableNameCell';
import { propertyNamesService } from '../../../services/propertyNamesService';
import { propertiesService } from '../../../services/propertiesService';
import DeleteItemIcon from '../../../shared/DeleteItemIcon';
import RestoreItemIcon from '../../../shared/RestoreItemIcon';
import EditItemIcon from '../../../shared/EditItemIcon';
import { propertyDeleteService } from '../../../services/propertyDeleteService';
import { POPUP_KEY_DELETE_PROPERTY, POPUP_KEY_RESTORE_PROPERTY } from '../../../constant';
import useWindowDimensions from '../../../hooks/useWindowDimension';

type PropertyRow = {
    id: string;
    propertyName: string;
    propertyType?: string;
    categoryName?: string;
    isVitamin?: boolean;
    isDeleted?: boolean;
};

type PropertyTableProps = {
    items: PropertyRow[];
    isLoading?: boolean;
};

const PropertyTable = ({ items, isLoading = false }: PropertyTableProps) => {
    const { i18n } = useTranslation();
    const navigate = useNavigate();
    const { width } = useWindowDimensions();

    const columns = useMemo<ListTableColumn<PropertyRow>[]>(() => [
        {
            id: 'name',
            header: i18n.t('app.page.listView.name'),
            cell: (row) => (
                <ListTableNameCell itemCode={row} itemService={propertyNamesService} />
            ),
        },
        {
            id: 'code',
            header: i18n.t('app.page.listView.code'),
            className: 'list-table__td_code',
            cell: (row) => <span className="list-table__code">{row.propertyName}</span>,
        },
        {
            id: 'propertyType',
            header: i18n.t('app.page.product.propertyItem.propertyType'),
            cell: (row) => (
                row.propertyType
                    ? i18n.t(propertiesService.getPropertyTypei18nCode(row))
                    : <span className="product__list-content_warning">{i18n.t('app.page.product.notAssigned')}</span>
            ),
        },
        {
            id: 'category',
            header: i18n.t('app.page.product.propertyItem.categoryName'),
            cell: (row) => (
                <div>
                    {row.categoryName
                        ? i18n.t(propertiesService.getPropertyCategoryi18nCode(row))
                        : <span className="product__list-content_warning">{i18n.t('app.page.product.notAssigned')}</span>}
                    {row.isVitamin ? (
                        <p className="list-table__muted">
                            {i18n.t(propertiesService.getPropertyVitaminTypei18nCode(row))}
                        </p>
                    ) : null}
                </div>
            ),
        },
        {
            id: 'status',
            header: i18n.t('app.page.listView.status'),
            className: 'list-table__td_status',
            cell: (row) => (
                row.isDeleted
                    ? <span className="list-table__chip">{i18n.t('app.page.listView.deleted')}</span>
                    : null
            ),
        },
        {
            id: 'actions',
            header: i18n.t('app.page.listView.actions'),
            className: 'list-table__td_actions',
            cell: (row) => (
                <ListTableActions>
                    {row.isDeleted ? (
                        <RestoreItemIcon itemId={row.id} popupKey={POPUP_KEY_RESTORE_PROPERTY} />
                    ) : (
                        <EditItemIcon pageUrl={`/product/property/${row.id}/`} />
                    )}
                    <DeleteItemIcon
                        itemId={row.id}
                        itemDeleteService={propertyDeleteService}
                        popupKey={POPUP_KEY_DELETE_PROPERTY}
                    />
                </ListTableActions>
            ),
        },
    ], [i18n]);

    return (
        <ListTable
            columns={columns}
            rows={items}
            rowKey={(row) => row.id}
            width={width}
            isLoading={isLoading}
            isRowDeleted={(row) => Boolean(row.isDeleted)}
            onRowClick={(row) => {
                if (!row.isDeleted) {
                    navigate(`/product/property/${row.id}/`);
                }
            }}
        />
    );
};

export default PropertyTable;
